// @ts-check
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/services/hooks";
import { ToastContainer } from "react-toastify";
import { useParams } from "react-router-dom";
import {
  getPageById,
  clearPage,
  getPagesByCurrentSite,
} from "@/services/pageServices/pageAction";
import {
  addHistoriesPages,
  getHistoriesPagesByCurrentSite,
} from "@/services/historiesPagesServices/historiesPagesAction";
import { getTemplatesByDesign } from "@/services/templateServices/templateAction";
import {
  getProductPropertiesById,
  getProductPropertiesByProductId,
} from "@/services/productPropertiesServices/ProductPropertiesActions";
import { getUsers } from "@/services/userServices/userActions";
import { getAllProperties } from "@/services/productServices/productActions";
import NotificationAlertOptions from "@/layouts/Alerts";
import UpdatePageView from "./UpdatePageView";
import { refusePageTypePayload } from "@/services/pageServices/pagesSlice";
import type { PageTypeSelection } from "../PageTypesUpdateContainer";
import {
  mergePageContentWithTemplate,
  derivePageConfiguration,
  normalizeRedirectFromText,
  redirectFromHasOwnPath,
  describeRefusedInputs,
} from "./updatePageHelpers";
import { usePageContentEditors } from "./usePageContentEditors";
import { asCaught } from "@/services/caughtError";

// Stable fallback for the users selector below (see useVariableOptions.js).
const EMPTY = [];

const UpdatePageContainer = () => {
  const params = useParams();
  const dispatch = useAppDispatch();

  // SCRUM-691: subscribe per slice instead of selecting the whole store.
  // useAppSelector((s) => s) returned the root state, so the container re-rendered
  // on every dispatch anywhere in the app and tripped the React-Redux whole-state
  // warning — which kept the container untestable end-to-end. Each selector below
  // reads only its own slice, so unrelated dispatches no longer re-render it.
  const CurrentSite = useAppSelector((s) => s.sites.site);
  const pageToUpdate = useAppSelector((s) => s.pages.page_to_update);
  const templatesByDesign = useAppSelector((s) => s.templates.templates_by_design_site);
  const updateMessage = useAppSelector((s) => s.historiespages.message);
  // `?? EMPTY` (SCRUM-1024): a failed getUsers used to park null here, which made
  // the fetch-if-empty effect below re-dispatch on every render. EMPTY is a stable
  // module constant so the fallback adds no re-renders.
  const users = useAppSelector((s) => s.users.users ?? EMPTY);
  // SCRUM-728: the site's pages feed the internal-link picker on Link content
  // variables. `?? EMPTY` for the same reason as `users` above (SCRUM-1024) —
  // a stable fallback keeps the fetch-if-empty effect from re-dispatching on
  // every render while the slice holds null after a failed fetch.
  const pages = useAppSelector((s) => s.pages.pages ?? EMPTY);
  const properties = useAppSelector((s) => s.products.properties ?? EMPTY);

  // SCRUM-2216: the page-type pick lives here. It was a field of the pages
  // slice, which nothing reset, so the header/footer and history editors
  // (HandleContent without this screen's page-type step) narrowed their
  // product options by the category last opened here.
  const [pageTypeSelection, setPageTypeSelection] = useState<PageTypeSelection>({});
  const [page, setPage] = useState<any>({}); // any: no client type for the page document yet; the form edits its fields by name
  const [oldPage, setOldPage] = useState<any>({}); // any: snapshot of the same untyped page document, compared at save
  const [redirect, setRedirect] = useState<string | null>(null);
  // SCRUM-1587: the "Redirect from" textarea holds free text while the editor
  // types (one path per line); it is normalised to the stored array only at
  // save, so a half-typed line is never posted as a redirect.
  const [redirectFromText, setRedirectFromText] = useState("");
  const [isPageUpdated, setIsPageUpdated] = useState(false);
  const [formchecker, setFormchecker] = useState({
    introTxt: false,
    globalTxt: false,
    metaKeywords: false,
    metaDescription: false,
    page_configuration: false,
    redirectFrom: false,
  });
  const [showCheckerResult, setShowCheckerResult] = useState(false);
  const [localUsers, setLocalUsers] = useState<{ label: any, value: any }[]>([]); // any: content-editor options built from the users slice; same untyped option shape as HandleContent (optional)

  const pageid = params.id;

  const updateDateOnUpdate = useCallback(() => {
    if (
      page?.page_template?.name === "midnightExpressProduct" ||
      page?.page_template?.name === "midnightExpressListing"
    ) {
      const idx = page?.page_content?.findIndex((i) => i.variable === "date");
      if (idx !== -1) {
        const updatedPageContent = [...page.page_content];
        updatedPageContent[idx] = {
          ...updatedPageContent[idx],
          value: new Date().toISOString().split("T")[0],
        };
        setPage((prev) => ({ ...prev, page_content: updatedPageContent }));
      }
    }
  }, [page]);

  const validInputsChecker = useCallback(() => {
    const checker = {
      introTxt: false,
      globalTxt: false,
      metaKeywords: false,
      metaDescription: false,
      page_configuration: false,
      redirectFrom: false,
    };

    if (!page?.global_title?.length) checker.globalTxt = true;
    if (!page?.meta_description?.length) checker.metaDescription = true;
    if (!page?.page_configuration?.type) checker.page_configuration = true;
    // SCRUM-1587: a redirect onto the page's own (stored) path is a loop.
    if (
      redirectFromHasOwnPath(
        normalizeRedirectFromText(redirectFromText),
        oldPage?.page_configuration?.path
      )
    ) {
      checker.redirectFrom = true;
    }

    setFormchecker(checker);
    return !(
      checker.introTxt ||
      checker.globalTxt ||
      checker.metaDescription ||
      checker.metaKeywords ||
      checker.page_configuration ||
      checker.redirectFrom
    );
  }, [page, redirectFromText, oldPage?.page_configuration?.path]);

  const onChangeFormInputHandler = useCallback((e) => {
    const { name, value } = e.target;
    setPage((prev) => ({ ...prev, [name]: value }));
  }, []);

  const onChangeCheckInputHandler = useCallback((e) => {
    const value = e.target.checked;
    setPage((prev) => ({ ...prev, sub_pages: value }));
  }, []);

  const onChangeRedirectFrom = useCallback((e) => {
    setRedirectFromText(e.target.value);
  }, []);

  // SCRUM-1362: keyed on the input's own `name`, unlike the handler above,
  // which writes `sub_pages` whatever it is handed. That one is left alone
  // rather than generalised: its call site passes `name="enabled"`, so making
  // it name-driven would change which field that toggle writes.
  const onChangeCheckboxByName = useCallback((e) => {
    const { name, checked } = e.target;
    setPage((prev) => ({ ...prev, [name]: !!checked }));
  }, []);

  const onProductSortChange = useCallback((product_sort) => {
    setPage((prev) => ({ ...prev, product_sort }));
  }, []);

  // The five page_content edit handlers live in one hook now (SCRUM-910);
  // each handler's behaviour is preserved verbatim.
  const {
    handleRequiredVariableChange,
    handleOptionalVariableChange,
    onChangeContentOptional,
    onChangeVariableValue,
    handleLinkInputRequired,
  } = usePageContentEditors(setPage);

  const SelectTemplatesInputHandler = useCallback((option) => {
    const value = option ? option.value : {};
    if (value?.design) {
      // Merge existing values with new template structure
      setPage((prev) => {
        const mergedContent = mergePageContentWithTemplate(prev.page_content, value);
        return {
          ...prev,
          page_template: value,
          page_content: mergedContent,
        };
      });
    } else {
      setPage((prev) => ({
        ...prev,
        page_template: value,
        page_content: [],
      }));
    }
  }, []);

  // Thin wrapper: the per-type path/object_target derivation is the pure
  // derivePageConfiguration (updatePageHelpers.js, SCRUM-910); only the setPage
  // stays here.
  const preparePageConfiguration = useCallback((pageTypeConf) => {
    setPage((prev) => ({
      ...prev,
      page_configuration: derivePageConfiguration(pageTypeConf),
    }));
  }, [setPage]);

  const UpdatePageHandler = useCallback(


    async (e) => {
      e.preventDefault();

      // Until SCRUM-1197 a blog_index save first PUT the blog's posts onto the
      // page (`updatePostsInBlogIndex`). The blog index resolves its articles
      // at read time since SCRUM-1097, so the save is the same as for any page.
      updateDateOnUpdate();

      const ok = validInputsChecker();
      if (ok) {
        const pageState = {
          ...page,
          product_sort: Object.prototype.hasOwnProperty.call(page, "product_sort")
            ? page.product_sort
            : null,
          // SCRUM-1587: always sent, so an emptied field clears the stored
          // array (`[]`) instead of leaving the old redirects in place.
          redirect_from: normalizeRedirectFromText(redirectFromText),
          page_configuration: {
            ...page.page_configuration,
            path: oldPage?.page_configuration?.path,
          },
        };

        // Only confirm + redirect when the staging POST actually succeeded.
        // addHistoriesPages now re-throws on failure (SCRUM-755), so a server-side
        // error no longer falls through to a silent navigation to /updated-pages.
        try {
          await dispatch(addHistoriesPages(pageState));
          setIsPageUpdated(true);
          setRedirect("/admin/updated-pages");
        } catch (err) {
          // SCRUM-1587: the server refuses an old path another page of this
          // site already redirects, in the DUPLICATE_PATH shape (SCRUM-1364).
          const data = asCaught(err)?.response?.data;
          NotificationAlertOptions(
            "error",
            data?.error === "DUPLICATE_REDIRECT_FROM"
              ? `Another page of this site already redirects from "${data.path}". Remove it there first.`
              : data?.message || "Updating the page failed. Please try again."
          );
        }
      } else {
        setShowCheckerResult(true);
        // SCRUM-1637: the red lines live next to the fields, several screens
        // above the button that was clicked; without this the refusal read as
        // "nothing happened" (measured on bancompara.pt, 2026-09-16). Same
        // channel as the save-error path above.
        NotificationAlertOptions(
          "error",
          `The page was not saved: ${describeRefusedInputs(page, redirectFromText, oldPage)}.`
        );
      }
    },
    [dispatch, oldPage, page, redirectFromText, updateDateOnUpdate, validInputsChecker]
  );

 
  useEffect(() => {
    // Both routes that render this container carry `:id` (routes/Routes.js),
    // so `pageid` is always set; the guard states that for the type-checker.
    if (!pageid) return;
    (async () => {
      await dispatch(getPageById(pageid));
    })();
  }, [dispatch, pageid]);

  // when CurrentSite has design, fetch templates
  useEffect(() => {
    if (CurrentSite?.design?._id) {
      dispatch(getTemplatesByDesign(CurrentSite.design._id));
    }
  }, [dispatch, CurrentSite?.design?._id]);

  // Fetch users for authorname dropdown
  useEffect(() => {
    if (users.length === 0) {
      dispatch(getUsers());
    }
    // length, not the array itself: an empty response would otherwise re-trigger
    // the fetch forever (SCRUM-571). SCRUM-1024: `users` is now always an array,
    // so this dep can no longer go undefined — the old `!users_redux` form
    // re-dispatched on EVERY render while the slice held null (a failed fetch),
    // hammering the endpoint that had just failed.
  }, [dispatch, users.length]);

  // Fetch the site's pages for the internal-link picker (SCRUM-728). Same
  // length-not-reference guard as the users effect above (SCRUM-571). Two
  // children of this screen already dispatch this thunk on their own mount
  // (useDropdownOptions.js:83 via HandleContent (required), and
  // the former Optional host (.jsx):175), so this is not the only caller — it is
  // here so the picker's data does not depend on a sibling being rendered.
  useEffect(() => {
    if (pages.length === 0) {
      dispatch(getPagesByCurrentSite());
    }
  }, [dispatch, pages.length]);

  useEffect(() => {
    if (page?.page_configuration?.type === "sub_category") {
      dispatch(getAllProperties());
    }
  }, [dispatch, page?.page_configuration?.type]);

  // Prepare localUsers for authorname dropdown (content editors only)
  useEffect(() => {
    if (users.length > 0) {
      const contentEditors = users
        .filter((user) => user.role === "CONTENTEDITOR")
        .map((user) => ({ label: user.name, value: user.name }));
      setLocalUsers(contentEditors);
    }
  }, [users]);

 


  // The write boundary moved here with the state (it was the store action's prepare,
  // SCRUM-855 / SCRUM-1650): a category / sub_category pick without its array
  // is reported and the previous pick is kept. A useCallback on purpose:
  // PageTypesUpdateContainer lists this handler in the deps of its stored-target
  // replay, so its identity must not change between renders.
  const onPageTypeChange = useCallback((selection: PageTypeSelection) => {
    if (refusePageTypePayload(selection)) return;
    setPageTypeSelection(selection);
  }, []);

  useEffect(() => {
    preparePageConfiguration(pageTypeSelection);
  }, [pageTypeSelection, preparePageConfiguration]);

  useEffect(() => {
    if (pageToUpdate) {
      // Merge page_content with template variables to ensure all variables are shown with correct types
      const mergedContent = mergePageContentWithTemplate(
        pageToUpdate.page_content,
        pageToUpdate.page_template
      );

      setPage({
        ...pageToUpdate,
        page_content: mergedContent
      });
      setOldPage({ ...pageToUpdate });
      setRedirectFromText((pageToUpdate.redirect_from ?? []).join("\n"));
    }
  }, [pageToUpdate]);


  // Notify on a successful update via the non-blocking toast mechanism instead of
  // a blocking native alert() (SCRUM-755): the alert was fragile for automation
  // (had to be dialog-handled) and inconsistent with the rest of the app.
  // SCRUM-883: the old `errors === "err"` info branch is removed — no thunk has
  // dispatched that sentinel since addHistoriesPages moved to real server messages
  // (SCRUM-755), so it was dead code. Genuine save errors are caught + toasted by
  // UpdatePageHandler and surfaced by the global notification hub.
  useEffect(() => {
    if (!isPageUpdated) return;
    NotificationAlertOptions("success", updateMessage);
  }, [isPageUpdated, updateMessage]);

// clear after quitting
useEffect(() => {
  return () => {
    dispatch(clearPage());
    dispatch(getHistoriesPagesByCurrentSite());
  };
}, [dispatch]);


  const selectTemplates = useMemo(
    () => (templatesByDesign || []).map((t) => ({ label: t.name, value: t })),
    [templatesByDesign]
  );

  return (
    <>
      <div className="rna-wrapper">
        <ToastContainer />
      </div>

      <UpdatePageView
        redirect={redirect}
        CurrentSite={CurrentSite}
        page={page}
        pageTypeSelection={pageTypeSelection}
        onPageTypeChange={onPageTypeChange}
        showCheckerResult={showCheckerResult}
        formchecker={formchecker}
        selectTemplates={selectTemplates}
        localUsers={localUsers}
        pages={pages}
        onChangeFormInputHandler={onChangeFormInputHandler}
        onChangeCheckInputHandler={onChangeCheckInputHandler}
        onChangeCheckboxByName={onChangeCheckboxByName}
        redirectFromText={redirectFromText}
        onChangeRedirectFrom={onChangeRedirectFrom}
        onProductSortChange={onProductSortChange}
        properties={properties}
        SelectTemplatesInputHandler={SelectTemplatesInputHandler}
        onChangeContentOptional={onChangeContentOptional}
        onChangeVariableValue={onChangeVariableValue}
        handleLinkInputRequired={handleLinkInputRequired}
        handleRequiredVariableChange={handleRequiredVariableChange}
        handleOptionalVariableChange={handleOptionalVariableChange}
        UpdatePageHandler={UpdatePageHandler}
        oldPage={oldPage}
      />
    </>
  );
};

export default UpdatePageContainer;
